import * as sodium from './sodium-redux';
import * as aes from './aes';
import { btoh, fromUint8Array, htob, toUint8Array } from './util.js';

const privateE2C = (key: string) => {
  const privateKey = new Uint8Array(sodium.crypto_scalarmult_BYTES);
  const keyBytes = htob(key);
  sodium.crypto_sign_ed25519_sk_to_curve25519(privateKey, keyBytes);
  return privateKey;
};

const publicE2C = (key: string) => {
  const publicKey = new Uint8Array(sodium.crypto_sign_PUBLICKEYBYTES);
  const keyBytes = htob(key);
  sodium.crypto_sign_ed25519_pk_to_curve25519(publicKey, keyBytes);
  return publicKey;
};

export const encrypt = (data: string, key: string) => {
  const cipher = new Uint8Array(data.length + sodium.crypto_box_SEALBYTES);
  const dataBytes = toUint8Array(data);
  const publicKey = publicE2C(key);
  sodium.crypto_box_seal(cipher, dataBytes, publicKey);
  return btoh(cipher);
};

export const decrypt = (
  encryptedData: string,
  publicKey: string,
  encryptedKey: string,
  key: string
) => {
  try {
    const privateKey = aes.decrypt(encryptedKey, key);
    const signPublicKey = publicE2C(publicKey);
    const signPrivateKey = privateE2C(privateKey + publicKey);
    const encryptedDataBytes = htob(encryptedData);
    const decrypted = new Uint8Array(
      encryptedDataBytes.length - sodium.crypto_box_SEALBYTES
    );
    const result = sodium.crypto_box_seal_open(
      decrypted,
      encryptedDataBytes,
      signPublicKey,
      signPrivateKey
    );
    if (result) {
      return fromUint8Array(decrypted);
    } else {
      throw new Error();
    }
  } catch {
    throw new Error('Could not decrypt with this pin');
  }
};

export const getSignature = (
  data: string,
  publicKey: string,
  encryptedSigningKey: string,
  key: string
) => {
  const signingKey = aes.decrypt(encryptedSigningKey, key);
  if (!signingKey) return null;
  const dataBytes = toUint8Array(data);
  const keyBytes = htob(signingKey + publicKey);
  const signature = new Uint8Array(sodium.crypto_sign_BYTES);
  sodium.crypto_sign_detached(signature, dataBytes, keyBytes);
  return btoh(signature);
};

export const sign = (
  data: string,
  publicKey: string,
  encryptedSigningKey: string,
  key: string
) => {
  const signingKey = aes.decrypt(encryptedSigningKey, key);
  if (!signingKey) return null;
  const dataBytes = toUint8Array(data);
  const keyBytes = htob(signingKey + publicKey);
  const signedData = new Uint8Array(dataBytes.length + sodium.crypto_sign_BYTES);
  sodium.crypto_sign(signedData, dataBytes, keyBytes);
  return btoh(signedData);
};

export const signOpen = (signedData: string, publicKey: string) => {
  const signedDataBytes = htob(signedData);
  const openedData = new Uint8Array(signedDataBytes.length - sodium.crypto_sign_BYTES);
  const keyBytes = htob(publicKey);
  const result = sodium.crypto_sign_open(openedData, signedDataBytes, keyBytes);
  return result ? openedData.toString() : null;
};

export const verifySignature = (
  signature: string,
  message: string,
  publicKey: string
) => {
  const messageBytes = toUint8Array(message);
  const signatureBytes = htob(signature);
  const keyBytes = htob(publicKey);
  return sodium.crypto_sign_verify_detached(signatureBytes, messageBytes, keyBytes);
};
